import Router from 'next/router'
import React from 'react'

import { WorkspaceContext } from '../WorkspaceContext'
import { ampli } from '@/ampli'
import { SplashScreen } from '@/components/ui/SplashScreen'
import { ENVIRONMENT } from '@/config/ui'
import { HttpMethods } from '@/config/urls'
import { accountUrls } from '@/config/urls/account'
import { useHttpDefaultApiHeaders } from '@/hooks/useHttpDefaultApiHeaders'
import { useLoggedUser } from '@/hooks/useLoggedUser'
import { useWorkspaces } from '@/hooks/useWorkspaces'
import { useWorkspaceUser } from '@/hooks/useWorkspaceUser'
import { PageMetadata } from '@/pages/_app'
import { httpFetch } from '@/utils/http'

type WorkspaceProviderProps = {
  children: React.ReactNode
  pageMetadata: PageMetadata
  shouldInitializeAmplitude: boolean
}

export function WorkspaceProvider({
  children,
  pageMetadata,
  shouldInitializeAmplitude,
}: WorkspaceProviderProps): JSX.Element {
  const { token } = useHttpDefaultApiHeaders()
  const { data: user, isLoading: isLoadingUser } = useLoggedUser(pageMetadata)
  const userId = user?.id

  const {
    current: workspace,
    data: workspaces,
    isLoading: isLoadingWorkspaces,
    mutate,
    setWorkspaceId,
  } = useWorkspaces(pageMetadata)

  const isAmplitudeAvailable =
    workspace?.descriptor?.amplitude_tracking &&
    workspace?.descriptor?.amplitude_tracking === 'AVAILABLE'
  const workspaceOnboardingStatus = workspace?.on_boarding_status
  const workspaceId = workspace?.id
  const workspaceName = workspace?.name
  const workspaceStatus = workspace?.status
  const isAmplitudeDisabled =
    !shouldInitializeAmplitude || // This is an external check that overrides the below ones
    (workspaceStatus === 'ACTIVE_CUSTOMER' && !isAmplitudeAvailable) // For FREEMIUM and TRIAL always track workspace?.descriptor?.amplitude_tracking
  const { data: membership, isLoading: isLoadingMembership } = useWorkspaceUser(
    workspace?.id,
    userId
  )

  const isLoading = isLoadingUser || isLoadingWorkspaces || isLoadingMembership
  const workspaceRole = membership?.role

  React.useEffect(() => {
    if (!isLoading) {
      ampli.load({
        client: {
          configuration: {
            // we need to check isAmplitudeDisabled otherwise we will keep tracking default events even when disabled
            defaultTracking: !isAmplitudeDisabled && {
              formInteractions: false,
              pageViews: false,
            },
          },
        },
        disabled: isAmplitudeDisabled,
        environment: ENVIRONMENT.isProduction ? 'production' : 'development',
      })
      if (!isAmplitudeDisabled) {
        Router.events.on('routeChangeComplete', () => {
          ampli.pageViewed({
            pathname: Router.pathname,
            ...getSegments(Router.asPath),
          })
        })
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoading])

  React.useEffect(() => {
    if (workspaceId) {
      httpFetch(
        accountUrls.getPing({
          workspaceId,
        }),
        { method: HttpMethods.PATCH, xsrfToken: token }
      ) // PING call to update last activity date for the current account
    }
  }, [workspaceId, token])

  React.useEffect(() => {
    if (
      !isLoading &&
      !isAmplitudeDisabled &&
      userId &&
      workspaceId &&
      workspaceName &&
      workspaceOnboardingStatus &&
      workspaceRole &&
      workspaceStatus
    ) {
      ampli.identify(userId, {
        workspaceId,
        workspaceName,
        workspaceOnboardingStatus,
        workspaceRole,
        workspaceStatus,
      })
      const pathname = Router.pathname
      ampli.pageViewed({
        pathname,
        ...getSegments(pathname),
      })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    isAmplitudeDisabled,
    isLoading,
    userId,
    workspaceId,
    workspaceName,
    workspaceOnboardingStatus,
    workspaceRole,
    workspaceStatus,
  ])

  // Public pages may not load the user and workspace. Therefor there is no need for waiting
  if (
    isLoading ||
    (!pageMetadata?.isPublic && !(membership && user && workspace))
  ) {
    return <SplashScreen />
  }

  return (
    <WorkspaceContext.Provider
      value={{
        membership,
        mutate,
        setWorkspaceId,
        user,
        workspace,
        workspaceId,
        workspaces,
      }}
    >
      {children}
    </WorkspaceContext.Provider>
  )
}

function getSegments(pathname: string): { pathnameSegment1 } {
  return pathname.split('/').reduce(
    (acc, segment, index) => {
      if (index > 0) {
        // the pathname starts with one slash
        acc[`pathnameSegment${index}`] = segment
      }
      return acc
    },
    {} as { pathnameSegment1 }
  )
}
